import {
  IMessageToHost,
  IMessageToHostApplicationStatusChange,
  IMessageToHostToasterHide,
  IMessageToHostToasterShow,
  ISingleSpaProps,
  MessageToHostType,
  PayloadStatus,
} from '@adnoc/servicebox';
import { LocationStrategy } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { endsWith } from 'lodash-es';
import { ActiveToast, Toast, ToastrService } from 'ngx-toastr';
import { Observable, firstValueFrom, from, shareReplay } from 'rxjs';
import { ParcelConfig, mountRootParcel } from 'single-spa';

import { SANDBOX_ROUTES } from '../constants';
import { getModuleConfig } from '@lms-approval/onehub-sandbox';
import { SandboxMessageToHostToasterStatus } from '../../../../../libs/onehub-sandbox/src/lib/enums';
import { ConfigService } from './config.service';
import { TokenService } from './token.service';

@Injectable()
export class MicroFrontendService {
  readonly isRemoteEntryLoaded$: Observable<boolean>;
  private toastMap = new Map<string, ActiveToast<Toast>>();

  constructor(
    private locationStrategy: LocationStrategy,
    private router: Router,
    private toastrService: ToastrService,
    private configService: ConfigService,
    private tokenService: TokenService,
  ) {
    this.isRemoteEntryLoaded$ = this.getIsRemoteEntryLoaded$();
  }

  getRemoteProps(customProps: {
    basename: string;
    isMobile: boolean;
    isInSandbox: boolean ;
  }) {
    const basename = this.getBaseHref(customProps.basename);
    const messageToHostFunction = this.getMessageToHostFunction();

    return {
      messageToHostFunction,
      name: '',
      singleSpa: '',
      mountParcel: mountRootParcel,
      options: {
        acquireToken: (scope: string) => firstValueFrom(this.tokenService.getToken$(scope)),
        basename,
        isMobile: customProps.isMobile,
        isInitiate: false,
        requestId: '',
        desktopPageSafeArea: { top: 88, left: 144 },
        isMaxPinned: false,
        isMobileKeyboardOpen: false,
        mobilePageSafeArea: { top: 0, left: 0, bottom: 0, right: 0 },
        name: '',
        singleSpa: '',
        mountParcel: mountRootParcel,
        logger: {
          log: () => null,
          verbose: () => null,
          information: () => null,
          warning: () => null,
          error: () => null,
          critical: () => null,
        },
        isInSandbox: customProps.isInSandbox,
      },
    };
  }

  getModuleConfig$(moduleName: string, uniqueName: string): Observable<ParcelConfig> {
    return from(getModuleConfig(moduleName, uniqueName));
  }

  private getBaseHref(internalPath: string): string {
    let baseHref = this.locationStrategy.getBaseHref();

    if (!endsWith(baseHref, '/')) {
      baseHref += '/';
    }

    return `${baseHref}${internalPath}`;
  }

  private getIsRemoteEntryLoaded$(): Observable<boolean> {
    return this.loadRemoteEntry$().pipe(shareReplay());
  }

  private loadRemoteEntry$(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const scriptElement = document.createElement('script');

      scriptElement.setAttribute('src', this.configService.remoteEntryUrl);
      scriptElement.onload = () => {
        subscriber.next(true);
        subscriber.complete();
      };
      scriptElement.onerror = () => {
        subscriber.next(false);
        subscriber.complete();
      };

      document.head.appendChild(scriptElement);
    });
  }

  private getMessageToHostFunction(): ISingleSpaProps['messageToHostFunction'] {
    return (hostMessage: IMessageToHost) => {
      const { type, payload } = hostMessage;

      switch (type) {
        case MessageToHostType.applicationStatusChange:
          this.redirectToComplete(payload as IMessageToHostApplicationStatusChange['payload']);
          break;
        case MessageToHostType.toasterHide:
          this.hideToast(payload as IMessageToHostToasterHide['payload']);
          break;
        case MessageToHostType.toasterShow:
          this.showToast(payload as IMessageToHostToasterShow['payload']);
          break;
        default:
          console.log(`Unsupported message type ${type}`, payload);
          break;
      }

      console.log('Host message', hostMessage);
    };
  }

  private redirectToComplete(hostMessagePayload: IMessageToHostApplicationStatusChange['payload']): void {
    if (hostMessagePayload.status !== PayloadStatus.success) {
      return;
    }

    if (hostMessagePayload.skipInboxItemRemoval) {
      return;
    }

    this.router.navigate(['/', SANDBOX_ROUTES.COMPLETE], { skipLocationChange: true });
  }

  private hideToast(hostMessagePayload: IMessageToHostToasterHide['payload']): void {
    const storedToast = this.toastMap.get(hostMessagePayload.id);

    if (!storedToast) {
      return;
    }

    this.toastrService.remove(storedToast.toastId);
  }

  private showToast(hostMessagePayload: IMessageToHostToasterShow['payload']): void {
    const statuses = Object.values(SandboxMessageToHostToasterStatus);

    if (!hostMessagePayload.status || !statuses.includes(hostMessagePayload.status as SandboxMessageToHostToasterStatus)) {
      const errorMessageStatuses = statuses.map((status) => `"${status}"`).join(', ');

      throw Error(`Toast type must be one of the following values: ${errorMessageStatuses}`);
    }

    let toastrMethod: 'success' | 'error' | 'info' | 'warning' | 'show';

    switch (hostMessagePayload.status) {
      case SandboxMessageToHostToasterStatus.success:
        toastrMethod = 'success';
        break;
      case SandboxMessageToHostToasterStatus.error:
        toastrMethod = 'error';
        break;
      case SandboxMessageToHostToasterStatus.info:
        toastrMethod = 'info';
        break;
      case SandboxMessageToHostToasterStatus.warning:
        toastrMethod = 'warning';
        break;
      default:
        toastrMethod = 'show';
    }

    const toast: ActiveToast<Toast> = this.toastrService[toastrMethod](hostMessagePayload.message);

    this.toastMap.set(hostMessagePayload.id, toast);
  }
}
